/**
 * Debriefing Export Modal Component
 *
 * Provides export options for ORCA360 CBTA Debriefing Guide
 * Supports both PDF and Word document formats
 *
 * Export Structure (Parts A-E):
 * - Part A: Session Details (from session data)
 * - Part B: Framework (STATIC CBTA theory - always available)
 * - Part C: Debrief Structure & Facilitator Questions (from database part_b)
 * - Part D: Supporting the Debriefing (from database part_c)
 * - Part E: Instructor Notes - Confidential (from database part_d)
 */

import { useState, useEffect } from 'react';
import { usePDFExportDebriefing } from '../hooks/usePDFExportDebriefing';
import { useWordExportDebriefing } from '../hooks/useWordExportDebriefing';
import { getPartADataForSession, checkPartACompleteness } from '../lib/debriefing-retrieval';
import { supabase } from '../lib/supabase';
import type { DebriefingPartAData, DebriefingExportOptions } from '../types/debriefing-export';
import type {
  PartB_DebriefingFramework,
  PartC_SupportingMaterial,
  PartD_InstructorNotes,
} from '../types/debriefing-guide';

interface DebriefingExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

interface DebriefingGuideData {
  partB: PartB_DebriefingFramework | null;
  partC: PartC_SupportingMaterial | null;
  partD: PartD_InstructorNotes | null;
  invocationLine: string | null;
}

export default function DebriefingExportModal({
  isOpen,
  onClose,
  sessionId,
}: DebriefingExportModalProps) {
  // Export options - Parts A-E mapping:
  // includePartA → Part A: Session Details
  // includePartB → Part B: Framework (STATIC - always available)
  // includePartC → Part C: Debrief Structure (from database part_b)
  // includePartD → Part D: Supporting the Debriefing (from database part_c)
  // includePartE → Part E: Instructor Notes (from database part_d)
  const [exportOptions, setExportOptions] = useState<DebriefingExportOptions & { includePartE?: boolean }>({
    format: 'pdf',
    includeLogo: true,
    includeHeaders: true,
    includeFooters: true,
    includePageNumbers: true,
    includePartA: true,
    includePartB: true, // Part B: Framework (STATIC - always available)
    includePartC: true, // Part C: Debrief Structure (database part_b)
    includePartD: true, // Part D: Supporting (database part_c)
    includePartE: true, // Part E: Instructor Notes (database part_d)
  });

  const [loading, setLoading] = useState(true);
  const [partAData, setPartAData] = useState<DebriefingPartAData | null>(null);
  const [guideData, setGuideData] = useState<DebriefingGuideData | null>(null);
  const [completeness, setCompleteness] = useState<{
    isComplete: boolean;
    missingFields: string[];
    completionPercentage: number;
  } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  // Parts availability mapping:
  // partB: Framework - ALWAYS true (static content)
  // partC: Debrief Structure - requires database part_b
  // partD: Supporting - requires database part_c
  // partE: Instructor Notes - requires database part_d
  const [partsAvailable, setPartsAvailable] = useState({
    partB: true, // Part B: Framework is STATIC - always available
    partC: false, // Part C: Debrief Structure (from database part_b)
    partD: false, // Part D: Supporting (from database part_c)
    partE: false, // Part E: Instructor Notes (from database part_d)
  });

  // Debriefing export hooks — used for both Part A-only and full (Parts A-E) exports
  const {
    exportDebriefingToPDF,
    isExporting: isPDFFullExporting,
  } = usePDFExportDebriefing({
    onExportSuccess: (blob, filename) => {
      downloadFile(blob, filename);
      onClose();
    },
    onExportError: (error) => {
      setExportError(error.message);
    },
    includePartA: exportOptions.includePartA,
    includePartB: exportOptions.includePartB, // Part B: Framework (STATIC)
    includePartC: exportOptions.includePartC, // Part C: Debrief Structure
    includePartD: exportOptions.includePartD, // Part D: Supporting
    includePartE: exportOptions.includePartE, // Part E: Instructor Notes
  });

  const {
    exportDebriefingToWord,
    isExporting: isWordFullExporting,
  } = useWordExportDebriefing({
    onExportSuccess: (blob, filename) => {
      downloadFile(blob, filename);
      onClose();
    },
    onExportError: (error) => {
      setExportError(error.message);
    },
    includePartA: exportOptions.includePartA,
    includePartB: exportOptions.includePartB, // Part B: Framework (STATIC)
    includePartC: exportOptions.includePartC, // Part C: Debrief Structure
    includePartD: exportOptions.includePartD, // Part D: Supporting
    includePartE: exportOptions.includePartE, // Part E: Instructor Notes
  });

  const isExporting = isPDFFullExporting || isWordFullExporting;

  // Load all data when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      loadAllData();
    }
  }, [isOpen, sessionId]);

  const loadAllData = async () => {
    setLoading(true);
    setExportError(null);

    try {
      // Load Part A data
      const { data, validation } = await getPartADataForSession(sessionId);
      setPartAData(data);

      // Check Part A completeness
      const completenessCheck = await checkPartACompleteness(sessionId);
      setCompleteness(completenessCheck);

      if (!validation.isValid) {
        setExportError(
          `Part A data is incomplete. Missing fields: ${validation.missingFields.join(', ')}`
        );
      }

      // Load Parts B, C, D from debriefing_guides table
      const { data: debriefingGuide, error: guideError } = await supabase
        .from('debriefing_guides')
        .select('part_b, part_c, part_d, invocation_line')
        .eq('session_id', sessionId)
        .single();

      if (guideError && guideError.code !== 'PGRST116') {
        console.error('Error loading debriefing guide:', guideError);
      }

      if (debriefingGuide) {
        // Map database fields to export data:
        // database part_b → export Part C (Debrief Structure)
        // database part_c → export Part D (Supporting)
        // database part_d → export Part E (Instructor Notes)
        setGuideData({
          partB: debriefingGuide.part_b as PartB_DebriefingFramework | null, // → Part C
          partC: debriefingGuide.part_c as PartC_SupportingMaterial | null,   // → Part D
          partD: debriefingGuide.part_d as PartD_InstructorNotes | null,      // → Part E
          invocationLine: debriefingGuide.invocation_line,
        });

        // Check which parts are available
        // Part B (Framework) is STATIC - always available
        // Part C requires database part_b
        // Part D requires database part_c
        // Part E requires database part_d
        setPartsAvailable({
          partB: true, // Part B: Framework is STATIC - always available
          partC: !!debriefingGuide.part_b, // Part C from database part_b
          partD: !!debriefingGuide.part_c, // Part D from database part_c
          partE: !!debriefingGuide.part_d, // Part E from database part_d
        });
      } else {
        setGuideData(null);
        // Part B is STATIC - always available even without database data
        setPartsAvailable({ partB: true, partC: false, partD: false, partE: false });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setExportError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!partAData) {
      setExportError('No data available for export');
      return;
    }

    setExportError(null);

    // Determine if we need full export or Part A only
    // Full export is needed if any part beyond Part A is selected
    const needsFullExport =
      exportOptions.includePartB || // Part B: Framework (STATIC - always available)
      (exportOptions.includePartC && partsAvailable.partC) || // Part C from database part_b
      (exportOptions.includePartD && partsAvailable.partD) || // Part D from database part_c
      (exportOptions.includePartE && partsAvailable.partE);   // Part E from database part_d

    try {
      // Build export data — partB/C/D are optional; omitting them produces a Part A-only document
      const exportData = {
        partA: partAData,
        partB: needsFullExport && exportOptions.includePartC && partsAvailable.partC && guideData?.partB ? guideData.partB : undefined,
        partC: needsFullExport && exportOptions.includePartD && partsAvailable.partD && guideData?.partC ? guideData.partC : undefined,
        partD: needsFullExport && exportOptions.includePartE && partsAvailable.partE && guideData?.partD ? guideData.partD : undefined,
        invocationLine: needsFullExport ? (guideData?.invocationLine || undefined) : undefined,
      };

      if (exportOptions.format === 'pdf') {
        await exportDebriefingToPDF(exportData, sessionId);
      } else {
        await exportDebriefingToWord(exportData, sessionId);
      }
    } catch (error) {
      console.error('Export error:', error);
      // Error is handled by the hooks
    }
  };

  const downloadFile = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const togglePart = (part: 'includePartB' | 'includePartC' | 'includePartD' | 'includePartE') => {
    setExportOptions(prev => ({ ...prev, [part]: !prev[part] }));
  };

  if (!isOpen) return null;

  // Calculate overall completion including Parts C-E (B is always available)
  const hasAnyDatabaseParts = partsAvailable.partC || partsAvailable.partD || partsAvailable.partE;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Export Debriefing Guide</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isExporting}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading session data...</span>
            </div>
          ) : (
            <>
              {/* Completeness Status */}
              {completeness && (
                <div className={`mb-6 p-4 rounded-lg ${
                  completeness.isComplete
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">
                      Part A Data Completeness
                    </h3>
                    <span className={`text-sm font-semibold ${
                      completeness.isComplete ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      {completeness.completionPercentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        completeness.isComplete ? 'bg-green-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${completeness.completionPercentage}%` }}
                    />
                  </div>
                  {completeness.missingFields.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Missing fields:</p>
                      <ul className="text-xs text-gray-500 mt-1">
                        {completeness.missingFields.map((field, index) => (
                          <li key={index}>• {field}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Export Format Selection */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Export Format</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setExportOptions({ ...exportOptions, format: 'pdf' })}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      exportOptions.format === 'pdf'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">📄</div>
                    <div className="font-medium">PDF Document</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Best for printing and archival
                    </div>
                  </button>

                  <button
                    onClick={() => setExportOptions({ ...exportOptions, format: 'docx' })}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      exportOptions.format === 'docx'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">📝</div>
                    <div className="font-medium">Word Document</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Best for editing and collaboration
                    </div>
                  </button>
                </div>
              </div>

              {/* Parts Selection */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Parts to Include
                </h3>
                <div className="space-y-2">
                  {/* Part A - Always included and enabled */}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includePartA}
                      disabled
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      Part A - Session Details
                      <span className="ml-2 text-xs text-green-600 font-medium">(Required)</span>
                    </span>
                  </label>

                  {/* Part B - Framework (STATIC - always available) */}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includePartB}
                      onChange={() => togglePart('includePartB')}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      Part B - Framework (CBTA Theory)
                      <span className="ml-2 text-xs text-green-600 font-medium">✓ Always Available</span>
                    </span>
                  </label>

                  {/* Part C - Debrief Structure (from database part_b) */}
                  <label className={`flex items-center ${!partsAvailable.partC ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={exportOptions.includePartC && partsAvailable.partC}
                      disabled={!partsAvailable.partC}
                      onChange={() => togglePart('includePartC')}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      Part C - Debrief Structure & Facilitator Questions
                      {partsAvailable.partC ? (
                        <span className="ml-2 text-xs text-green-600 font-medium">✓ Available</span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-400">(Not generated yet)</span>
                      )}
                    </span>
                  </label>

                  {/* Part D - Supporting the Debriefing (from database part_c) */}
                  <label className={`flex items-center ${!partsAvailable.partD ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={exportOptions.includePartD && partsAvailable.partD}
                      disabled={!partsAvailable.partD}
                      onChange={() => togglePart('includePartD')}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      Part D - Supporting the Debriefing
                      {partsAvailable.partD ? (
                        <span className="ml-2 text-xs text-green-600 font-medium">✓ Available</span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-400">(Not generated yet)</span>
                      )}
                    </span>
                  </label>

                  {/* Part E - Instructor Notes (from database part_d) */}
                  <label className={`flex items-center ${!partsAvailable.partE ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={exportOptions.includePartE && partsAvailable.partE}
                      disabled={!partsAvailable.partE}
                      onChange={() => togglePart('includePartE')}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      Part E - Instructor Notes
                      {partsAvailable.partE ? (
                        <span className="ml-2 text-xs text-green-600 font-medium">✓ Available</span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-400">(Not generated yet)</span>
                      )}
                    </span>
                    {partsAvailable.partE && (
                      <span className="ml-2 text-xs text-red-500 font-medium">(Confidential)</span>
                    )}
                  </label>
                </div>

                {/* Info message about Parts C-E (B is always available) */}
                {!hasAnyDatabaseParts && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> Parts C, D, and E are generated when the AI processes the session transcript.
                      Generate the debriefing guide first to include these sections in the export.
                    </p>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {exportError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{exportError}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={loading || isExporting || !partAData}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  Export {exportOptions.format === 'pdf' ? 'PDF' : 'Word'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
